import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import { API_BASE_URL } from '../config/api';
import Footer from '../components/Footer';
import { getPillars, getThemes, getSubThemes } from '../data/taxonomy';
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText, 
  DollarSign, 
  TrendingUp,
  Users,
  Download
} from 'lucide-react';

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const [stats, setStats] = useState({ totalPapers: 0, totalDownloads: 0, totalRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/research-library');
      return;
    }
    fetchPapers();
  }, [user, authLoading, navigate]);

  const fetchPapers = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/papers`,
        { params: { limit: 100 } }
      );
      setPapers(data);
      
      // Calculate stats
      const totalDownloads = data.reduce((sum, p) => sum + p.downloads, 0);
      const totalRevenue = data.reduce((sum, p) => sum + (p.price * p.downloads), 0);
      setStats({
        totalPapers: data.length,
        totalDownloads,
        totalRevenue
      });
    } catch (error) {
      console.error('Error fetching papers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (paperId) => {
    if (!window.confirm('Are you sure you want to archive this paper?')) return;
    
    try {
      await axios.delete(
        `${API_BASE_URL}/api/papers/${paperId}`,
        { withCredentials: true }
      );
      alert('Paper archived successfully');
      fetchPapers();
    } catch (error) {
      alert('Failed to archive paper');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-gray-500">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Blue ribbon page header */}
      <div className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white pt-28 pb-10">
        <div className="container mx-auto px-4 flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-dashboard-title">Admin Dashboard</h1>
            <p className="text-gray-200 mt-2">Manage research library and government schemes</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <a
              href="/admin/analytics"
              className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white rounded-md font-medium flex items-center gap-2 transition-colors"
              data-testid="admin-analytics-link"
            >
              📈 Analytics
            </a>
            <a
              href="/admin/authors"
              className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white rounded-md font-medium flex items-center gap-2 transition-colors"
            >
              <Users className="h-5 w-5" />
              Manage Authors
            </a>
            <a
              href="/admin/schemes"
              className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white rounded-md font-medium flex items-center gap-2 transition-colors"
            >
              <FileText className="h-5 w-5" />
              Manage Schemes
            </a>
            <a
              href="/admin/upload"
              className="px-6 py-3 bg-[#E87722] hover:bg-[#d66a1a] text-white rounded-md font-medium flex items-center gap-2 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Upload New Paper
            </a>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Papers</p>
                <p className="text-3xl font-bold text-[#1E3A5F] mt-2">{stats.totalPapers}</p>
              </div>
              <FileText className="h-12 w-12 text-[#E87722]" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total Downloads</p>
                <p className="text-3xl font-bold text-[#1E3A5F] mt-2">{stats.totalDownloads}</p>
              </div>
              <Download className="h-12 w-12 text-[#E87722]" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Estimated Revenue</p>
                <p className="text-3xl font-bold text-[#1E3A5F] mt-2">₹{stats.totalRevenue.toFixed(0)}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-[#E87722]" />
            </div>
          </Card>
        </div>

        {/* Papers Table */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-[#1E3A5F] mb-6">All Research Papers</h2>
          
          {papers.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No papers yet. Upload your first paper!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Title</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Pillar</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Theme</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Price</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Downloads</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {papers.map((paper) => (
                    <tr key={paper.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <div className="max-w-xs">
                          <p className="font-medium text-gray-900 truncate">{paper.title}</p>
                          <p className="text-sm text-gray-500 truncate">{paper.description.substring(0, 60)}...</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="px-3 py-1 bg-[#1E3A5F] text-white text-xs rounded-full">
                          {paper.pillar}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-700">{paper.theme}</td>
                      <td className="py-4 px-4 font-semibold text-[#E87722]">₹{paper.price}</td>
                      <td className="py-4 px-4 text-gray-700">{paper.downloads}</td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/edit-paper/${paper.id}`)}
                            className="text-blue-600 border-blue-600 hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(paper.id)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Footer />
    </div>
  );
}

// Paper Upload Form Component
function PaperUploadForm({ onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pillar: '',
    theme: '',
    sub_themes: [],
    authors: '',
    publication_date: '',
    price: '',
    keywords: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [availableThemes, setAvailableThemes] = useState([]);
  const [availableSubThemes, setAvailableSubThemes] = useState([]);

  const pillars = getPillars();

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If pillar changes, reset theme and sub_themes
    if (name === 'pillar') {
      setFormData({ ...formData, pillar: value, theme: '', sub_themes: [] });
      setAvailableThemes(getThemes(value));
      setAvailableSubThemes([]);
    }
    // If theme changes, reset sub_themes
    else if (name === 'theme') {
      setFormData({ ...formData, theme: value, sub_themes: [] });
      setAvailableSubThemes(getSubThemes(formData.pillar, value));
    }
    else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubThemeToggle = (subTheme) => {
    const currentSubThemes = formData.sub_themes;
    if (currentSubThemes.includes(subTheme)) {
      setFormData({ 
        ...formData, 
        sub_themes: currentSubThemes.filter(st => st !== subTheme) 
      });
    } else {
      setFormData({ 
        ...formData, 
        sub_themes: [...currentSubThemes, subTheme] 
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Parse comma-separated values
      const payload = {
        ...formData,
        sub_themes: formData.sub_themes, // Already an array
        authors: formData.authors.split(',').map(s => s.trim()).filter(Boolean),
        keywords: formData.keywords.split(',').map(s => s.trim()).filter(Boolean),
        price: parseFloat(formData.price),
        publication_date: formData.publication_date ? new Date(formData.publication_date).toISOString() : null
      };

      await axios.post(
        `${API_BASE_URL}/api/papers/`,
        payload,
        { withCredentials: true }
      );

      alert('Paper uploaded successfully!');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload paper');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Title */}
        <div className="md:col-span-2">
          <Label htmlFor="title">Paper Title *</Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="Enter paper title"
          />
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <Label htmlFor="description">Description *</Label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
            placeholder="Detailed description of the research paper"
          />
        </div>

        {/* Pillar */}
        <div>
          <Label htmlFor="pillar">Pillar *</Label>
          <select
            id="pillar"
            name="pillar"
            value={formData.pillar}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent"
          >
            <option value="">Select pillar</option>
            {pillars.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Theme */}
        <div>
          <Label htmlFor="theme">Theme *</Label>
          <select
            id="theme"
            name="theme"
            value={formData.theme}
            onChange={handleChange}
            required
            disabled={!formData.pillar}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E87722] focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">Select theme</option>
            {availableThemes.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {!formData.pillar && (
            <p className="text-xs text-gray-500 mt-1">Select a pillar first</p>
          )}
        </div>

        {/* Sub-themes */}
        <div className="md:col-span-2">
          <Label>Sub-themes (select multiple)</Label>
          {availableSubThemes.length > 0 ? (
            <div className="border border-gray-300 rounded-md p-4 max-h-48 overflow-y-auto bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableSubThemes.map(st => (
                  <label key={st} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.sub_themes.includes(st)}
                      onChange={() => handleSubThemeToggle(st)}
                      className="rounded border-gray-300 text-[#E87722] focus:ring-[#E87722]"
                    />
                    <span className="text-sm text-gray-700">{st}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-3 px-4 bg-gray-50 rounded-md border border-gray-200">
              {formData.theme ? 'No sub-themes available' : 'Select a theme to see sub-themes'}
            </p>
          )}
          {formData.sub_themes.length > 0 && (
            <p className="text-xs text-gray-600 mt-2">
              Selected: {formData.sub_themes.length} sub-theme{formData.sub_themes.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Authors */}
        <div>
          <Label htmlFor="authors">Authors (comma-separated) *</Label>
          <Input
            id="authors"
            name="authors"
            value={formData.authors}
            onChange={handleChange}
            required
            placeholder="e.g., Dr. Smith, Prof. Kumar"
          />
        </div>

        {/* Publication Date */}
        <div>
          <Label htmlFor="publication_date">Publication Date</Label>
          <Input
            id="publication_date"
            name="publication_date"
            type="date"
            value={formData.publication_date}
            onChange={handleChange}
          />
        </div>

        {/* Price */}
        <div>
          <Label htmlFor="price">Price (₹) *</Label>
          <Input
            id="price"
            name="price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={handleChange}
            required
            placeholder="499"
          />
        </div>

        {/* Keywords */}
        <div className="md:col-span-2">
          <Label htmlFor="keywords">Keywords (comma-separated)</Label>
          <Input
            id="keywords"
            name="keywords"
            value={formData.keywords}
            onChange={handleChange}
            placeholder="e.g., MSME, Economic Recovery, India"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <Button
          type="submit"
          disabled={submitting}
          className="bg-[#E87722] hover:bg-[#d66a1a] text-white"
        >
          {submitting ? 'Uploading...' : 'Upload Paper'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>

      <p className="text-sm text-gray-500 mt-4">
        * Required fields. PDF upload will be added in the next phase.
      </p>
    </form>
  );
}
